import JSONModel from "sap/ui/model/json/JSONModel";
import BaseController from "./BaseController";
import { ErrorHandler } from "../service/ErrorHandler.service";
import { EnvironmentService } from "../service/Environment.service";
import { Artikel, Artikelkategorisierung } from "../model/models";
import ResizeHandler from "sap/ui/core/ResizeHandler";
import { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import Carousel, { Carousel$PageChangedEvent } from "sap/m/Carousel";
import Control from "sap/ui/core/Control";
import Router from "sap/ui/core/routing/Router";

/**
 * @namespace com.unitrade.crm.controller
 */
export default class ArticleCompare extends BaseController {
  private SCREEN_MAX_SIZES = {
    PHONE: 600,
    TABLET: 1024,
  };

  private ITEMS_COUNT_PER_SCREEN_SIZE = {
    PHONE: 1,
    TABLET: 2,
    DESKTOP: 4,
  };

  private _oRootControl: Control;
  private _oRouter: Router;
  private _dataModel: JSONModel;
  private _settingsModel: JSONModel;

  private _oCarouselExpanded: Carousel;
  private _oCarouselSnapped: Carousel;
  private _iAllProductsCount: number;
  private _iPagesCount: number;
  private _isDesktop: boolean;
  private _iFirstArticleIndex: number;
  private _iLastArticleIndex: number;
  private _propGroup: { [id: number]: { eigenschaft: Artikelkategorisierung; werte: Artikelkategorisierung[] } };

  public override async getTitle(): Promise<string> {
    return await this.getTranslatedString("ArticleCompare");
  }

  onInit(): void {
    const oOwnerComponent = this.getOwnerComponent();
    this._oRootControl = oOwnerComponent.getRootControl();
    this._oRouter = oOwnerComponent.getRouter();
    this._oRouter.getRoute("articleCompare").attachPatternMatched(this._onPatternMatch, this);
  }

  onAfterRendering(): void {
    this._oCarouselSnapped = this.getView().byId("carousel-snapped") as Carousel;
    this._oCarouselExpanded = this.getView().byId("carousel-expanded") as Carousel;
  }

  private _onPatternMatch = async (oEvent: Route$PatternMatchedEvent) => {
    this._dataModel = new JSONModel();
    this._settingsModel = new JSONModel();
    this.setModel(this._dataModel, "data");
    this.setModel(this._settingsModel, "settings");
    this._propGroup = null;

    const args = oEvent.getParameter("arguments") as {
      idsPath: string;
    };
    const ids = args.idsPath.split(",").map((sId) => parseInt(sId));
    const article = await this._loadArticle(ids);
    this._dataModel.setProperty("/article", article);
    this._iAllProductsCount = article.length;
    this._iPagesCount = this._getPagesCount(this._oRootControl.$().innerWidth());
    this._settingsModel.setProperty("/pagesCount", this._iPagesCount);
    this._settingsModel.setProperty("/isDesktop", this._checkIsDesktop());
    this._iFirstArticleIndex = 0;
    this._iLastArticleIndex = this._iPagesCount - 1;

    this._setVisibleArticles();
    this._setArticleProperties();
    ResizeHandler.register(this._oRootControl, this._onResize.bind(this));
  };

  private _onResize = async (oEvent: any) => {
    const pagesCount = this._getPagesCount(this._oRootControl.$().innerWidth());
    if (this._iPagesCount !== pagesCount) {
      this._iPagesCount = pagesCount;
      this._iFirstArticleIndex = 0;
      this._iLastArticleIndex = pagesCount - 1;
      this._isDesktop = this._checkIsDesktop();
      this._settingsModel.setProperty("/pagesCount", this._iPagesCount);
      this._settingsModel.setProperty("/isDesktop", this._isDesktop);

      this._updateFirstPage();
      this._setVisibleArticles();
      this._setArticleProperties();
      this._updateCarouselsActivePage();
    }
  };

  protected onPageChanged = (oEvent: Carousel$PageChangedEvent) => {
    const activePages: number[] = oEvent.getParameter("activePages");
    this._iFirstArticleIndex = activePages.at(0);
    this._iLastArticleIndex = activePages.at(-1);
    this._setVisibleArticles();
    this._setArticleProperties();
  };

  private _loadArticle = async (artids: number[]): Promise<Artikel[]> => {
    const oModel = this.getModel() as JSONModel;
    try {
      oModel.setProperty("/busy", true);
      const artikel = await Promise.all(
        artids.map((artid) => {
          if ([681200090, 681200067, 681200081, 681200096].includes(artid)) {
            return this._loadFakeArtikel(artid);
          } else {
            return this._callArticleGet(artid);
          }
        })
      );

      artikel.map((art) => {
        art.eigenschaften.push({
          aktiv: true,
          artikelId: art.id,
          eigenschaftId: -1,
          name: "Beschreibung",
          bezeichnung: "Artikelbeschreinung oder Artikeldrucktext",
          categorieAktiv: true,
          categorieBezeichnung: null,
          categorieId: 0,
          categorieName: null,
          defaultValue: null,
          displayWert: !art.beschreibungLang || !art.beschreibungLang.trim() ? art.artikeldrucktext : art.beschreibungLang,
          ebene: 0,
          einheitText: null,
          typ: "STRING",
          sortierung: -10, // show on top
        });
      });
      return artikel;
    } catch (ex: any) {
      ErrorHandler.handleError(ex);
    } finally {
      oModel.setProperty("/busy", false);
    }
  };

  private _setVisibleArticles(): void {
    const allArticle = this._dataModel.getProperty("/article") as Artikel[];
    const visibleArticle: Artikel[] = [];
    for (let index = this._iFirstArticleIndex; index <= this._iLastArticleIndex; index++) {
      visibleArticle.push(allArticle[index]);
    }

    this._dataModel.setProperty("/visibleArticle", visibleArticle);
    console.log(visibleArticle);
  }

  private _setArticleProperties = () => {
    if (!this._propGroup) {
      this._initPropGroup();
    }

    const visibleArticles = this._dataModel.getProperty("/visibleArticle") as Artikel[];
    Object.entries(this._propGroup).forEach(([key, value]) => {
      value.werte.length = 0;
      for (const article of visibleArticles) {
        value.werte.push(article.eigenschaften.find((a) => a.eigenschaftId == value.eigenschaft.eigenschaftId));
      }
    });

    this._dataModel.setProperty("/eigenschaften", this._propGroup);
    console.log(this._propGroup);
  };

  private _initPropGroup() {
    const allArticles = this._dataModel.getProperty("/article") as Artikel[];
    const allProps = allArticles
      .map((a) => a.eigenschaften)
      .flat()
      .sort(this._sortEigenschaft);
    this._propGroup = {};
    for (const prop of allProps) {
      if (!(prop.eigenschaftId in this._propGroup)) {
        this._propGroup[prop.eigenschaftId] = { eigenschaft: prop, werte: [] };
      }
    }

    this._dataModel.setProperty("/eigenschaften", this._propGroup);
  }

  private _sortEigenschaft(a: Artikelkategorisierung, b: Artikelkategorisierung): number {
    if (a.sortierung < b.sortierung) return -1;
    else if (a.sortierung > b.sortierung) return 1;
    return 0;
  }

  private _callArticleGet = (artid: number): JQuery.jqXHR<Artikel> => {
    return $.get(`${EnvironmentService.getBaseUrl()}/artikel/${artid}`);
  };

  private _getPagesCount = (iWidth: number): number => {
    let iPagesCount: number;

    if (iWidth <= this.SCREEN_MAX_SIZES.PHONE) {
      iPagesCount = this.ITEMS_COUNT_PER_SCREEN_SIZE.PHONE;
    } else if (iWidth <= this.SCREEN_MAX_SIZES.TABLET) {
      iPagesCount = this.ITEMS_COUNT_PER_SCREEN_SIZE.TABLET;
    } else {
      iPagesCount = this.ITEMS_COUNT_PER_SCREEN_SIZE.DESKTOP;
    }

    if (iPagesCount > this._iAllProductsCount) {
      iPagesCount = this._iAllProductsCount;
    }

    return iPagesCount;
  };

  private _checkIsDesktop() {
    return this._iPagesCount === this.ITEMS_COUNT_PER_SCREEN_SIZE.DESKTOP;
  }

  private _updateFirstPage() {
    if (this._iFirstArticleIndex + this._iPagesCount > this._iAllProductsCount) {
      this._iFirstArticleIndex = this._iAllProductsCount - this._iPagesCount;
      this._updateCarouselsActivePage();
    }
  }

  private _updateCarouselsActivePage = () => {
    this._oCarouselExpanded.setActivePage(this._oCarouselExpanded.getPages()[this._iFirstArticleIndex]);
    this._oCarouselSnapped.setActivePage(this._oCarouselSnapped.getPages()[this._iFirstArticleIndex]);
  };

  private _loadFakeArtikel = async (artid: number) => {
    let article: Artikel;
    if (artid == 681200090) {
      article = {
        art: "A",
        imageUrl: "https://www.globus-baumarkt.de/thumbnail/c9/cd/67/1671626739/04000420308713_90002300_1920x980.JPG",
        kurzbezeichnung: "ROLLER1",
        bezeichnung1: "blu:s Elektro-Roller XT2000",
        bezeichnung2: "25 km/h (Mofa-Klasse) rot ",
        ean: "2011101200037",
        listenEK: 0,
        id: 681200090,
        beschreibungLang: `Mehr Emotion, weniger Emission - der blu:s XT2000 ist ein lässiger E-Motorroller, der Platz für zwei Personen bietet. Der durchzugsstarke Elektromotor und die robuste Batterie ermöglichen ein sportliches Fahren bei hohem Komfort.

          Hinweis: Führerscheinpflicht Klasse S, Helmpflicht und Versicherungspflicht`,
        bereich: 6,
        warengruppe: 81,
        artikelgruppe: 20,
        artikelnummer: 90,
        mengeneinheitText: "Stk",
        basisVKExclMwst: 1300,
        basisVKInklMwst: 1500,
        artikeldrucktext: "",
        preiseinheit: 1.0,
        berechneterPreis: {
          gesamtPreisBrutto: 1499.0,
          gesamtPreisNetto: 1499.0,
        },
        eigenschaften: [
          {
            artikelId: 681200090,
            eigenschaftId: 3970,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Motor",
            bezeichnung: "Motor",
            aktiv: true,
            einheitText: "Watt",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "2000",
            sortierung: 0,
          },
          {
            artikelId: 681200090,
            eigenschaftId: 3971,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Höchstgeschwindigkeit",
            bezeichnung: "Höchstgeschwindigkeit",
            aktiv: true,
            einheitText: "km/h",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "25",
            sortierung: 1,
          },
          {
            artikelId: 681200090,
            eigenschaftId: 3972,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Gewicht",
            bezeichnung: "Gewicht",
            aktiv: true,
            einheitText: "kg",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "65",
            sortierung: 2,
          },
          {
            artikelId: 681200090,
            eigenschaftId: 3973,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Reichweite",
            bezeichnung: "Reichweite",
            aktiv: true,
            einheitText: "km",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "max. 59",
            sortierung: 3,
          },
          {
            artikelId: 681200090,
            eigenschaftId: 3974,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Maße (LxBxH)",
            bezeichnung: "Maße (LxBxH)",
            aktiv: true,
            einheitText: "",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "167 x 70 x 112 cm",
            sortierung: 6,
          },

          {
            artikelId: 681200090,
            eigenschaftId: 3975,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "zul. Gesamtgewicht",
            bezeichnung: "zul. Gesamtgewicht",
            aktiv: true,
            einheitText: "",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "ca. 249 kg (inkl. Eigengewicht des Fahrzeuges)",
            sortierung: 6,
          },
        ],
      };
    } else if (artid == 681200067) {
      article = {
        art: "A",
        imageUrl: "https://www.globus-baumarkt.de/thumbnail/11/78/5c/1669817744/4000420305064_1951564_1920x980.jpg",
        kurzbezeichnung: "ROLLER2",
        bezeichnung1: "LuXXon Elektro-Roller E3000",
        bezeichnung2: "45 km/h (Mokick-Klasse) weiß ",
        ean: "2016101230037",
        listenEK: 0,
        id: 681200067,
        beschreibungLang: `Die neue Baureihe E3000 wird durch einen bürstenlosen und wartungsfreien Hinterrad-Nabenmotor mit hohem Drehmoment angetrieben. Die Akkuzellen des E3000 sind fest eingebaut - Sie brauchen daher einen 230 Volt -Anschluss in Reichweite für den Ladevorgang.

        Der E3000 fährt in der Mofa-Version bei optimalen Bedingungen ca. 60 km weit, in der 45 km/h Version bis zu 50 km. Bei beiden Versionen sind ein Gepäckträger und ein Topcase in Fahrzeugfarbe serienmäßig an Bord. 
        
        Der E-Scooter ist lieferbar als 45 km/h und als 25 km/h (Mofa-)Version.
        
        Dieses Fahrzeug entspricht genau dem heutigen Zeitgeist - modern, klein, spritzig, leise und umweltfreundlich. Sie werden begeistert sein von den niedrigen Betriebskosten. Erleben Sie jetzt die neue Welt der emissionsfreien Fortbewegung.
        
        Keine KFZ-Steuer oder Anmeldung beim Straßenverkehrsamt nötig, lediglich Versicherungskennzeichen.
        Beim 45 km/h Modell benötigen Sie einen Mokick-Führerschein. 
        
        Es besteht eine Helmpflicht.`,
        bereich: 6,
        warengruppe: 81,
        artikelgruppe: 20,
        artikelnummer: 67,
        mengeneinheitText: "Stk",
        basisVKExclMwst: 1300,
        basisVKInklMwst: 1500,
        artikeldrucktext: "",
        preiseinheit: 1.0,
        berechneterPreis: {
          gesamtPreisBrutto: 1699.0,
          gesamtPreisNetto: 1699.0,
        },
        eigenschaften: [
          {
            artikelId: 681200067,
            eigenschaftId: 3970,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Motor",
            bezeichnung: "Motor",
            aktiv: true,
            einheitText: "Watt",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "3000",
            sortierung: 0,
          },
          {
            artikelId: 681200067,
            eigenschaftId: 3971,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Höchstgeschwindigkeit",
            bezeichnung: "Höchstgeschwindigkeit",
            aktiv: true,
            einheitText: "km/h",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "45",
            sortierung: 1,
          },
          {
            artikelId: 681200067,
            eigenschaftId: 3972,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Gewicht",
            bezeichnung: "Gewicht",
            aktiv: true,
            einheitText: "kg",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "68",
            sortierung: 2,
          },
          {
            artikelId: 681200067,
            eigenschaftId: 3973,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Reichweite",
            bezeichnung: "Reichweite",
            aktiv: true,
            einheitText: "km",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "max. 55",
            sortierung: 3,
          },
          {
            artikelId: 681200067,
            eigenschaftId: 3974,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Maße (LxBxH)",
            bezeichnung: "Maße (LxBxH)",
            aktiv: true,
            einheitText: "",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "175 x 67 x 109,5 cm",
            sortierung: 6,
          },

          {
            artikelId: 681200067,
            eigenschaftId: 3975,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "zul. Gesamtgewicht",
            bezeichnung: "zul. Gesamtgewicht",
            aktiv: true,
            einheitText: "",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "ca. 259 kg (inkl. Eigengewicht des Fahrzeuges)",
            sortierung: 6,
          },
        ],
      };
    } else if (artid == 681200081) {
      article = {
        art: "A",
        imageUrl: "https://www.globus-baumarkt.de/thumbnail/5c/32/e0/1695643209/4000420308508_3667952_1920x980.jpg",
        kurzbezeichnung: "ROLLER3",
        bezeichnung1: "blu:s Elektro-Roller XT2000",
        bezeichnung2: "45 km/h (Mokick-Klasse) blau",
        ean: "2016101230037",
        listenEK: 0,
        id: 681200081,
        beschreibungLang: `Mehr Emotion, weniger Emission - der blu:s XT2000 ist ein lässiger E-Motorroller, der Platz für zwei Personen bietet. Der durchzugsstarke Elektromotor und die robuste Batterie ermöglichen ein sportliches Fahren bei hohem Komfort.

        Hinweis: Führerscheinpflicht Klasse AM, Helmpflicht und Versicherungspflicht`,
        bereich: 6,
        warengruppe: 81,
        artikelgruppe: 20,
        artikelnummer: 81,
        mengeneinheitText: "Stk",
        basisVKExclMwst: 1300,
        basisVKInklMwst: 1500,
        artikeldrucktext: "",
        preiseinheit: 1.0,
        berechneterPreis: {
          gesamtPreisBrutto: 1499.0,
          gesamtPreisNetto: 1499.0,
        },
        eigenschaften: [
          {
            artikelId: 681200081,
            eigenschaftId: 3970,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Motor",
            bezeichnung: "Motor",
            aktiv: true,
            einheitText: "Watt",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "2000",
            sortierung: 0,
          },
          {
            artikelId: 681200081,
            eigenschaftId: 3971,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Höchstgeschwindigkeit",
            bezeichnung: "Höchstgeschwindigkeit",
            aktiv: true,
            einheitText: "km/h",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "45",
            sortierung: 1,
          },
          {
            artikelId: 681200081,
            eigenschaftId: 3972,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Gewicht",
            bezeichnung: "Gewicht",
            aktiv: true,
            einheitText: "kg",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "74",
            sortierung: 2,
          },
          {
            artikelId: 681200081,
            eigenschaftId: 3973,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Reichweite",
            bezeichnung: "Reichweite",
            aktiv: true,
            einheitText: "km",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "max. 50",
            sortierung: 3,
          },
          {
            artikelId: 681200081,
            eigenschaftId: 3974,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Maße (LxBxH)",
            bezeichnung: "Maße (LxBxH)",
            aktiv: true,
            einheitText: "",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "167 x 70 x 112 cm",
            sortierung: 6,
          },

          {
            artikelId: 681200081,
            eigenschaftId: 3975,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "zul. Gesamtgewicht",
            bezeichnung: "zul. Gesamtgewicht",
            aktiv: true,
            einheitText: "",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "ca. 249 kg (inkl. Eigengewicht des Fahrzeuges)",
            sortierung: 6,
          },
        ],
      };
    } else {
      article = {
        art: "A",
        imageUrl: "https://www.globus-baumarkt.de/thumbnail/2d/69/95/1671621324/4000420283331_3668021_1920x980.jpg",
        kurzbezeichnung: "ROLLER4",
        bezeichnung1: "blu:s Elektro-Scooter XT950",
        bezeichnung2: "20 KM/h anthrazit",
        ean: "2016101233200",
        listenEK: 0,
        id: 681200096,
        beschreibungLang: `Der straßenzugelassene blu:s XT950 mit ABE überzeugt mit seiner umfangreichen Ausstattung. Neben der großen 10" Luftbereifung für einen hohen Fahrkomfort auch auf unebenem Gelände verfügt der E-Scooter über einen 350 Watt starken Motor und eine 36 V/13 Ah Batterie, welche eine maximale Reichweite von bis zu 50 Kilometern ermöglicht.

        Weiterhin ist der S950 komplett faltbar, hat also neben der klappbaren und höhenverstellbaren Lenkstange auch einklappbare Griffe. Damit kann man ihn auch auf kleinster Fläche verstauen. Neben dem großen Trittbrett für einen sicheren Stand sorgen zwei Scheibenbremsen für eine zuverlässige Bremswirkung.`,
        bereich: 6,
        warengruppe: 81,
        artikelgruppe: 20,
        artikelnummer: 96,
        mengeneinheitText: "Stk",
        basisVKExclMwst: 1300,
        basisVKInklMwst: 1500,
        artikeldrucktext: "",
        preiseinheit: 1.0,
        berechneterPreis: {
          gesamtPreisBrutto: 799.0,
          gesamtPreisNetto: 799.0,
        },
        eigenschaften: [
          {
            artikelId: 681200096,
            eigenschaftId: 3970,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Motor",
            bezeichnung: "Motor",
            aktiv: true,
            einheitText: "Watt",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "350",
            sortierung: 0,
          },
          {
            artikelId: 681200096,
            eigenschaftId: 3971,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Höchstgeschwindigkeit",
            bezeichnung: "Höchstgeschwindigkeit",
            aktiv: true,
            einheitText: "km/h",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "20",
            sortierung: 1,
          },
          {
            artikelId: 681200096,
            eigenschaftId: 3972,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Gewicht",
            bezeichnung: "Gewicht",
            aktiv: true,
            einheitText: "kg",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "21,5",
            sortierung: 2,
          },
          {
            artikelId: 681200096,
            eigenschaftId: 3973,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Reichweite",
            bezeichnung: "Reichweite",
            aktiv: true,
            einheitText: "km",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "max. 50",
            sortierung: 5,
          },
          {
            artikelId: 681200096,
            eigenschaftId: 3974,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "Maße (LxBxH)",
            bezeichnung: "Maße (LxBxH)",
            aktiv: true,
            einheitText: "",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "121 x 58,5 x 102,5 cm",
            sortierung: 6,
          },

          {
            artikelId: 681200096,
            eigenschaftId: 3975,
            ebene: 0,
            categorieId: 0,
            categorieName: null,
            categorieBezeichnung: null,
            categorieAktiv: true,
            name: "zul. Gesamtgewicht",
            bezeichnung: "zul. Gesamtgewicht",
            aktiv: true,
            einheitText: "",
            typ: "NUMBER",
            defaultValue: null,
            displayWert: "ca. 120 kg (inkl. Eigengewicht des Fahrzeuges)",
            sortierung: 6,
          },
        ],
      };
    }

    return article;
  };
}
